import React, { useState, useEffect } from "react";
import { DEFAULT_BUNDLES } from "../../components/BundlesExplainer";
import { DEFAULT_SERIES_CARDS } from "../../components/SeriesCards";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { FiPlus, FiTrash2, FiUpload, FiSave } from "react-icons/fi";
import { compressImage } from "../../utils/imageUtils";
import toast from "react-hot-toast";

const EMPTY_SLIDE = { imageUrl: "", title: "", subtitle: "", buttonText: "", buttonLink: "", objectFit: "cover", objectPosition: "center" };
const EMPTY_TICKER = "";

export default function AdminHomepage() {
    const [bundles, setBundles] = useState(DEFAULT_BUNDLES);
    const [seriesCards, setSeriesCards] = useState(DEFAULT_SERIES_CARDS);
    const [slides, setSlides] = useState([]);
    const [ticker, setTicker] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    const [newTicker, setNewTicker] = useState("");
    const [sliderHeight, setSliderHeight] = useState(500);

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "homepage"));
                if (snap.exists()) {
                    const data = snap.data();
                    setSlides(data.bannerSlides || []);
                    setTicker(data.tickerItems || []);
                    setSliderHeight(data.sliderHeight || 500);
                    setBundles({ ...DEFAULT_BUNDLES, ...(data.bundleSection || {}) });
                    setSeriesCards(data.seriesCards && data.seriesCards.length > 0 ? data.seriesCards : DEFAULT_SERIES_CARDS);
                }
            } catch {
                toast.error("Could not load homepage config.");
            }
            setLoading(false);
        }
        load();
    }, []);

    async function save() {
        setSaving(true);
        try {
            await setDoc(doc(db, "siteConfig", "homepage"), { 
                bannerSlides: slides, 
                tickerItems: ticker,
                sliderHeight: parseInt(sliderHeight) || 500,
                bundleSection: bundles,
                seriesCards: seriesCards
            });
            toast.success("Homepage saved!");
        } catch {
            toast.error("Failed to save.");
        }
        setSaving(false);
    }

    /* ── Bundles section ── */
    function updateBundleCard(i, field, value) {
        setBundles(b => ({ ...b, cards: b.cards.map((c, idx) => idx === i ? { ...c, [field]: value } : c) }));
    }

    /* ── Series cards ── */
    function updateSeriesCard(i, field, value) {
        setSeriesCards(list => list.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
    }
    async function uploadSeriesImage(i, file) {
        if (!file) return;
        try {
            const compressed = await compressImage(file, 2000, true);
            const storageRef = ref(storage, "products/series/" + Date.now() + "_" + compressed.name);
            const task = uploadBytesResumable(storageRef, compressed);
            task.on("state_changed", null, () => toast.error("Upload failed."), async () => {
                const url = await getDownloadURL(task.snapshot.ref);
                updateSeriesCard(i, "imageUrl", url);
                toast.success("Series picture uploaded!");
            });
        } catch {
            toast.error("Error processing image.");
        }
    }

    /* ── Banner slides ── */
    function addSlide() { setSlides(s => [...s, { ...EMPTY_SLIDE }]); }
    function removeSlide(i) { setSlides(s => s.filter((_, idx) => idx !== i)); }
    function updateSlide(i, field, value) {
        setSlides(s => s.map((sl, idx) => idx === i ? { ...sl, [field]: value } : sl));
    }
    async function uploadSlideImage(i, file) {
        if (!file) return;
        setUploadingIdx(i);
        try {
            const compressed = await compressImage(file, 2000); // Higher res for banners
            const storageRef = ref(storage, `banners/${Date.now()}_${compressed.name}`);
            const task = uploadBytesResumable(storageRef, compressed);
            task.on("state_changed", null, (error) => { 
                console.error(error);
                toast.error("Upload failed."); 
                setUploadingIdx(null); 
            }, async () => {
                const url = await getDownloadURL(task.snapshot.ref);
                updateSlide(i, "imageUrl", url);
                setUploadingIdx(null);
                toast.success("Image uploaded!");
            });
        } catch (e) {
            toast.error("Compression failed.");
            setUploadingIdx(null);
        }
    }

    /* ── Ticker ── */
    function addTicker() {
        if (!newTicker.trim()) return;
        setTicker(t => [...t, newTicker.trim().toUpperCase()]);
        setNewTicker("");
    }
    function removeTicker(i) { setTicker(t => t.filter((_, idx) => idx !== i)); }

    if (loading) return <div className="admin-loading">Loading…</div>;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
            {/* Save */}
            <div className="admin-action-row" style={{ justifyContent: "flex-end" }}>
                <button className="admin-btn-primary" onClick={save} disabled={saving}>
                    <FiSave size={16} /> {saving ? "Saving…" : "Save All Changes"}
                </button>
            </div>

            {/* Banner Global Settings */}
            <div className="admin-card" style={{ padding: 24 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: 16 }}>Banner Global Settings</h3>
                <div className="admin-form-field" style={{ maxWidth: 200 }}>
                    <label>Slider Height (Desktop pixels)</label>
                    <input 
                        type="number" 
                        value={sliderHeight} 
                        onChange={e => setSliderHeight(e.target.value)} 
                        placeholder="500"
                    />
                </div>
            </div>

            {/* Ticker section */}
            <div className="admin-card" style={{ padding: 24 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: 20 }}>Announcement Ticker</h3>
                <div className="admin-action-row" style={{ marginBottom: 16 }}>
                    <input
                        value={newTicker}
                        onChange={e => setNewTicker(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && addTicker()}
                        placeholder="NEW RELEASE: 'DARK VOWS' OUT NOW"
                        style={{ flex: 1, padding: "10px 14px", border: "1.5px solid #e0ddd8", borderRadius: 8, fontSize: 14, outline: "none" }}
                    />
                    <button className="admin-btn-primary" onClick={addTicker}>
                        <FiPlus size={16} /> Add
                    </button>
                </div>
                {ticker.length === 0 && <p style={{ color: "#aaa", fontSize: 14 }}>No ticker items yet.</p>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {ticker.map((item, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "#f4f5f7", padding: "7px 12px", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                            {item}
                            <button onClick={() => removeTicker(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", lineHeight: 1 }}>
                                <FiTrash2 size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bundles section */}
            <div className="admin-card" style={{ padding: 24 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: 6 }}>Bundles Explainer</h3>
                <p style={{ color: "#888", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
                    Shown on the home page so customers know what each bundle includes.
                </p>
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 14, fontWeight: 600 }}>
                    <input type="checkbox" checked={bundles.enabled} onChange={e => setBundles(b => ({ ...b, enabled: e.target.checked }))} />
                    Show this section on the home page
                </label>
                <div className="admin-form-field" style={{ marginBottom: 14 }}>
                    <label>Heading</label>
                    <input value={bundles.heading} onChange={e => setBundles(b => ({ ...b, heading: e.target.value }))} />
                </div>
                <div className="admin-form-field" style={{ marginBottom: 20 }}>
                    <label>Intro</label>
                    <textarea rows={2} value={bundles.intro} onChange={e => setBundles(b => ({ ...b, intro: e.target.value }))} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #e0ddd8", fontFamily: "inherit", boxSizing: "border-box" }} />
                </div>
                {bundles.cards.map((card, i) => (
                    <div key={i} style={{ background: "#f9f9f9", borderRadius: 10, padding: 16, marginBottom: 12 }}>
                        <div className="admin-form-field" style={{ marginBottom: 10 }}>
                            <label>Card {i + 1} title</label>
                            <input value={card.title} onChange={e => updateBundleCard(i, "title", e.target.value)} />
                        </div>
                        <div className="admin-form-field">
                            <label>Card {i + 1} text</label>
                            <textarea rows={2} value={card.text} onChange={e => updateBundleCard(i, "text", e.target.value)} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #e0ddd8", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Series cards section */}
            <div className="admin-card" style={{ padding: 24 }}>
                <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: 6 }}>Series Cards</h3>
                <p style={{ color: "#888", fontSize: 13, marginTop: 0, marginBottom: 20 }}>
                    The three cards on the home page. Each one opens a page with the series' description and all its books in English and Spanish.
                    Series names must match the "Series" set on each book (separate several with commas).
                </p>
                {seriesCards.map((card, i) => (
                    <div key={i} style={{ background: "#f9f9f9", borderRadius: 10, padding: 16, marginBottom: 14 }}>
                        <div className="admin-form-field" style={{ marginBottom: 10 }}>
                            <label>Card title</label>
                            <input value={card.title} onChange={e => updateSeriesCard(i, "title", e.target.value)} />
                        </div>
                        <div className="admin-form-field" style={{ marginBottom: 10 }}>
                            <label>Page address (no spaces, e.g. kingsman)</label>
                            <input value={card.slug} onChange={e => updateSeriesCard(i, "slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
                        </div>
                        <div className="admin-form-field" style={{ marginBottom: 10 }}>
                            <label>Series names on the books</label>
                            <input value={(card.seriesNames || []).join(", ")} onChange={e => updateSeriesCard(i, "seriesNames", e.target.value.split(",").map(x => x.trim()).filter(Boolean))} placeholder="Kingsman, El Sr Kingsman" />
                        </div>
                        <div className="admin-form-field" style={{ marginBottom: 10 }}>
                            <label>Series description (world explanation)</label>
                            <textarea rows={4} value={card.description || ""} onChange={e => updateSeriesCard(i, "description", e.target.value)} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1.5px solid #e0ddd8", fontFamily: "inherit", boxSizing: "border-box" }} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 700, color: "#7c3aed" }}>Card background colour (behind the picture)</label>
                            <input type="color" value={card.background || "#1a1a2e"} onChange={e => updateSeriesCard(i, "background", e.target.value)} style={{ width: 48, height: 32, border: "none", background: "none", cursor: "pointer" }} />
                            <span style={{ fontSize: 12, color: "#888" }}>{card.background || "#1a1a2e"}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                            {card.imageUrl && <img src={card.imageUrl} alt="" style={{ height: 80, width: 120, objectFit: "cover", borderRadius: 8 }} />}
                            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#fff", border: "1.5px dashed #c4b5fd", padding: "8px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#7c3aed" }}>
                                {card.imageUrl ? "Replace picture" : "Upload picture"}
                                <input type="file" accept="image/*" onChange={e => uploadSeriesImage(i, e.target.files[0])} style={{ display: "none" }} />
                            </label>
                            {card.imageUrl && <button type="button" onClick={() => updateSeriesCard(i, "imageUrl", "")} style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", fontSize: 13 }}>Remove</button>}
                        </div>
                    </div>
                ))}
            </div>

            {/* Banner slides */}
            <div>
                <div className="admin-section-header">
                    <h3 style={{ fontFamily: "'Playfair Display', serif" }}>Hero Banner Slides</h3>
                    <button className="admin-btn-primary" onClick={addSlide}>
                        <FiPlus size={16} /> Add Slide
                    </button>
                </div>

                {slides.length === 0 && (
                    <div className="admin-card" style={{ padding: 32, textAlign: "center", color: "#aaa" }}>
                        No slides yet. Add your first banner slide.
                    </div>
                )}

                {slides.map((slide, i) => (
                    <div key={i} className="admin-card" style={{ marginBottom: 20, padding: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <h4 style={{ fontWeight: 700, color: "#333" }}>Slide {i + 1}</h4>
                            <button className="admin-btn-danger" onClick={() => removeSlide(i)}>
                                <FiTrash2 size={14} /> Remove
                            </button>
                        </div>

                        {/* Image */}
                        <div style={{ marginBottom: 16 }}>
                            {slide.imageUrl && (
                                <img src={slide.imageUrl} alt={`Slide ${i + 1}`} style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 10, marginBottom: 10 }} />
                            )}
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", background: "#f4f5f7", border: "1.5px dashed #ccc", padding: "9px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13, color: "#555" }}>
                                    <FiUpload size={15} />
                                    {uploadingIdx === i ? "Uploading…" : "Upload Image"}
                                    <input type="file" accept="image/*" onChange={e => uploadSlideImage(i, e.target.files[0])} style={{ display: "none" }} />
                                </label>
                                <span style={{ color: "#999", fontSize: 12 }}>or paste URL:</span>
                                <input
                                    value={slide.imageUrl}
                                    onChange={e => updateSlide(i, "imageUrl", e.target.value)}
                                    placeholder="https://..."
                                    style={{ flex: 1, padding: "9px 12px", border: "1.5px solid #e0ddd8", borderRadius: 8, fontSize: 13, outline: "none" }}
                                />
                            </div>
                        </div>

                        <div className="admin-form-grid">
                            <div className="admin-form-field">
                                <label>Title</label>
                                <input value={slide.title} onChange={e => updateSlide(i, "title", e.target.value)} placeholder="Welcome to LB Books" />
                            </div>
                            <div className="admin-form-field">
                                <label>Subtitle</label>
                                <input value={slide.subtitle} onChange={e => updateSlide(i, "subtitle", e.target.value)} placeholder="Bold stories for bold readers." />
                            </div>
                            <div className="admin-form-field">
                                <label>Button Text</label>
                                <input value={slide.buttonText} onChange={e => updateSlide(i, "buttonText", e.target.value)} placeholder="Shop Now" />
                            </div>
                            <div className="admin-form-field">
                                <label>Button Link</label>
                                <input value={slide.buttonLink} onChange={e => updateSlide(i, "buttonLink", e.target.value)} placeholder="/shop" />
                            </div>
                            <div className="admin-form-field">
                                <label>Image Fit (Size)</label>
                                <select 
                                    value={slide.objectFit || "cover"} 
                                    onChange={e => updateSlide(i, "objectFit", e.target.value)}
                                    style={{ padding: "10px 14px", border: "1.5px solid #e0ddd8", borderRadius: 8, fontSize: 14, outline: "none" }}
                                >
                                    <option value="cover">Cover (Fill area)</option>
                                    <option value="contain">Contain (Show whole image)</option>
                                </select>
                            </div>
                            <div className="admin-form-field">
                                <label>Image Position</label>
                                <select 
                                    value={slide.objectPosition || "center"} 
                                    onChange={e => updateSlide(i, "objectPosition", e.target.value)}
                                    style={{ padding: "10px 14px", border: "1.5px solid #e0ddd8", borderRadius: 8, fontSize: 14, outline: "none" }}
                                >
                                    <option value="center">Center</option>
                                    <option value="top">Top</option>
                                    <option value="bottom">Bottom</option>
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                </select>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
